import React from "react";

// The Halloween drawings, as SVG so they stay sharp at every size, weigh next
// to nothing and can move. Drawn in the site's own manner: flat colour, a
// plum outline, the occasional hard shadow. The 42courses mark itself is
// never drawn here; where it appears it is the real file.

const INK = "#2e2437";
const NIGHT = "#140e1a";

// A bat, about 26 x 12. Its wings flap in CSS (.hw-bat).
export const Bat = ({ x, y, scale = 1, delay = 0 }: { x: number; y: number; scale?: number; delay?: number }) => (
  <g transform={`translate(${x} ${y}) scale(${scale})`}>
    <g className="hw-bat" style={{ animationDelay: `${delay}s` }}>
      <path
        d="M13 6c-1.2-1.6-2.6-1.9-3.6-.8C8.2 2.6 5.2 1.5 0 2.4c2.6 1.2 3.4 3 3.2 5 1.6-.8 3.4-.6 4.6.8.9-1.1 2.4-1.4 3.6-.6.4.9.9 1.6 1.6 2.4.7-.8 1.2-1.5 1.6-2.4 1.2-.8 2.7-.5 3.6.6 1.2-1.4 3-1.6 4.6-.8-.2-2 .6-3.8 3.2-5-5.2-.9-8.2.2-9.4 2.8-1-1.1-2.4-.8-3.6.8z"
        fill={NIGHT}
      />
      <circle cx="13" cy="4.6" r="1.6" fill={NIGHT} />
      <path d="M11.9 3.4l.5-1.6.6 1.2.6-1.2.5 1.6z" fill={NIGHT} />
    </g>
  </g>
);

// A witch on her broomstick, flying right, in silhouette. Drawn in a 0..120
// frame and placed by the caller.
export const Witch = ({ fill = NIGHT }: { fill?: string }) => (
  <g fill={fill}>
    {/* Broom: stick and bristles, tied with a band. */}
    <path d="M10 53.5 116 40.5" stroke={fill} strokeWidth="3.4" strokeLinecap="round" />
    <path d="M14 53c-9-7-20-9-31-7 5 4 6 8 1 12 4 3 4 6-1 9 11 1 22-3 31-11z" />
    <path d="M12 49.5l3.6 6.6" stroke="#f5b041" strokeWidth="2" strokeLinecap="round" />
    {/* Cape, streaming behind. */}
    <path d="M52 27c-9 1-17 6-28 6 3 5 8 8 15 10-5 1-9 3-14 3 7 3 15 3 24 1z" />
    {/* Body and skirt, sitting on the broom. */}
    <path d="M45 49c0-13 8-25 19-26 9-1 14 8 13 22l-1 3z" />
    {/* Arm reaching forward to hold the stick. */}
    <path d="M66 30c6 3 11 6 16 10l-2 3.5c-6-3-11-6-16-8z" />
    {/* Leg and pointed boot. */}
    <path d="M64 45c7 2 14 5 20 8 3 0 6-1 8 0-1 2-3 4-7 3-7-1-14-3-21-6z" />
    {/* Head, nose and hair. */}
    <circle cx="69" cy="18" r="7.2" />
    <path d="M75 16.5c3 .6 5.6 1.8 7.6 3.2-2.6.6-5.2.4-7.6-.2z" />
    <path d="M63 16c-6 2-9 7-12 13 5-2 9-5 13-9z" />
    {/* Hat: brim and a cone with its tip bent back. */}
    <ellipse cx="69" cy="11.5" rx="13" ry="2.8" />
    <path d="M61.5 11.5h15c-1-6-2.4-11-5-15-2.8-3.6-7-5-10.5-4.6 3.4 2.4 4.6 6 3.6 10z" />
  </g>
);

// The star of the show: a pumpkin in a mortarboard. 120 x 120.
export const ScholarPumpkin = ({ glow = true }: { glow?: boolean }) => (
  <g>
    <ellipse cx="60" cy="113" rx="42" ry="5" fill="#000" opacity=".28" />
    {/* Lobes, back pair first. */}
    <ellipse cx="37" cy="78" rx="27" ry="32" fill="#e5600a" stroke={INK} strokeWidth="2.6" />
    <ellipse cx="83" cy="78" rx="27" ry="32" fill="#e5600a" stroke={INK} strokeWidth="2.6" />
    <ellipse cx="60" cy="78" rx="25" ry="34" fill="#ff7a1a" stroke={INK} strokeWidth="2.6" />
    <path d="M47 54c-4 8-5 17-4 27" stroke="#ffa552" strokeWidth="3" strokeLinecap="round" fill="none" opacity=".8" />
    <path d="M20 66c-2 6-2 12-1 18" stroke="#ff9a4a" strokeWidth="2.6" strokeLinecap="round" fill="none" opacity=".7" />
    {/* Carved face, lit from inside. */}
    <g fill={glow ? "#ffd166" : "#3a1505"} className={glow ? "hw-glow" : undefined}>
      <path d="M41 70l8-11 7 12z" />
      <path d="M64 71l7-12 8 11z" />
      <path d="M57 77l3-5 3 5z" />
      <path d="M36 84c8 13 40 13 48 0l-5 .4-3 4-4-3.6-4 4.6-4-4.6-4 4.6-4-4.6-4 3.6-3-4z" />
    </g>
    {/* Mortarboard, worn at a jaunty angle. */}
    <g transform="rotate(-9 60 40)">
      <path d="M43 47c11-5 23-5 34 0l-1.6 9c-10-4-21-4-31 0z" fill={INK} />
      <path d="M60 25 98 37 60 49 22 37z" fill={INK} stroke="#4a3c57" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M60 28 90 37.3 60 46.5 30 37.3z" fill="#3a2f47" />
      <path d="M60 37h27v18" stroke="#f5b041" strokeWidth="1.8" fill="none" strokeLinecap="round" />
      <path d="M84.6 54.5h4.8l1.4 9h-7.6z" fill="#f5b041" stroke="#c4841d" strokeWidth="1" />
      <circle cx="60" cy="37" r="2.6" fill="#f5b041" />
    </g>
  </g>
);

// A plain little pumpkin, for company. 60 x 60.
export const LittlePumpkin = ({ fill = "#ff7a1a", shade = "#e5600a" }: { fill?: string; shade?: string }) => (
  <g>
    <ellipse cx="30" cy="56" rx="22" ry="3" fill="#000" opacity=".25" />
    <path d="M30 14c0-5 3-9 7-10" stroke="#4f7a2c" strokeWidth="3.4" strokeLinecap="round" fill="none" />
    <ellipse cx="19" cy="35" rx="14" ry="18" fill={shade} stroke={INK} strokeWidth="2" />
    <ellipse cx="41" cy="35" rx="14" ry="18" fill={shade} stroke={INK} strokeWidth="2" />
    <ellipse cx="30" cy="35" rx="13" ry="19" fill={fill} stroke={INK} strokeWidth="2" />
  </g>
);

// The pumpkin in the middle of the wheel, with 42 carved in it and lit from
// inside. 100 x 92.
export const NumberPumpkin = () => (
  <g>
    <path d="M50 22c0-7 4-12 10-14" stroke="#4f7a2c" strokeWidth="4.6" strokeLinecap="round" fill="none" />
    <ellipse cx="29" cy="54" rx="26" ry="31" fill="#e5600a" stroke={INK} strokeWidth="2.6" />
    <ellipse cx="71" cy="54" rx="26" ry="31" fill="#e5600a" stroke={INK} strokeWidth="2.6" />
    <ellipse cx="50" cy="54" rx="30" ry="34" fill="#ff7a1a" stroke={INK} strokeWidth="2.6" />
    <path d="M10 42c-2 6-2 14 0 21" stroke="#ff9a4a" strokeWidth="2.6" strokeLinecap="round" fill="none" opacity=".7" />
    <g fontFamily="Open Sans, sans-serif" fontWeight="800" fontSize="36" textAnchor="middle" letterSpacing="-1">
      {/* The cut edge of the carving, then the candlelight showing through. */}
      <text x="50" y="68.5" fill="#8a3306">42</text>
      <text x="50" y="66.5" fill="#ffd166" className="hw-glow">42</text>
    </g>
  </g>
);

// A bare, crooked tree in silhouette, grown from a seed: each seed gives a
// different tree, and the same one on every visit. Its foot is at 0, 0 and it
// is roughly 300 tall at scale 1.
interface Branch {
  d: string;
  width: number;
}

const grownTrees: Record<number, Branch[]> = {};

const growTree = (seed: number): Branch[] => {
  if (grownTrees[seed]) return grownTrees[seed];

  let state = seed;
  const random = () => {
    state = (state * 16807) % 2147483647;
    return (state - 1) / 2147483646;
  };
  const branches: Branch[] = [];

  const branch = (x: number, y: number, angle: number, length: number, width: number, depth: number) => {
    const heading = angle + (random() - 0.5) * 0.6;
    const endX = x + Math.sin(heading) * length;
    const endY = y - Math.cos(heading) * length;
    // A kink part way along, so no branch is straight.
    const kink = (random() - 0.5) * length * 0.4;
    const midX = (x + endX) / 2 + Math.cos(heading) * kink;
    const midY = (y + endY) / 2 + Math.sin(heading) * kink;
    branches.push({
      d: `M${x.toFixed(1)} ${y.toFixed(1)}Q${midX.toFixed(1)} ${midY.toFixed(1)} ${endX.toFixed(1)} ${endY.toFixed(1)}`,
      width,
    });
    if (depth === 0) return;

    const forks = depth > 3 || random() < 0.45 ? 2 : 3;
    for (let fork = 0; fork < forks; fork += 1) {
      const spread = (fork - (forks - 1) / 2) * (0.5 + random() * 0.45);
      branch(endX, endY, heading + spread, length * (0.64 + random() * 0.16), width * 0.64, depth - 1);
    }
  };

  branch(0, 0, (random() - 0.5) * 0.2, 96, 18, 6);
  grownTrees[seed] = branches;
  return branches;
};

const SILHOUETTE = "#07050a";

export const SpookyTree = ({ seed, x, y, scale = 1 }: { seed: number; x: number; y: number; scale?: number }) => (
  <g transform={`translate(${x} ${y}) scale(${scale})`}>
    <g stroke={SILHOUETTE} strokeLinecap="round" fill="none">
      {growTree(seed).map((branch, index) => (
        <path key={index} d={branch.d} strokeWidth={branch.width} />
      ))}
    </g>
    {/* The trunk flares into roots where it meets the ground. */}
    <path d="M-30 4c12-3 18-12 20-34h20c2 22 8 31 20 34z" fill={SILHOUETTE} />
  </g>
);

// The cauldron the witch makes your card vanish into. 140 x 110.
export const Cauldron = ({ bubbling = false }: { bubbling?: boolean }) => (
  <g className={bubbling ? "hw-cauldron is-bubbling" : "hw-cauldron"}>
    <ellipse cx="70" cy="104" rx="50" ry="5" fill="#000" opacity=".3" />
    <g className="hw-bubbles" fill="#8be04e" stroke={INK} strokeWidth="1.6">
      <circle cx="52" cy="30" r="7" />
      <circle cx="78" cy="22" r="9" />
      <circle cx="94" cy="34" r="5" />
      <circle cx="64" cy="14" r="4" />
    </g>
    <path d="M30 50c-6 0-8 6-4 9M110 50c6 0 8 6 4 9" stroke={INK} strokeWidth="5" fill="none" strokeLinecap="round" />
    <path d="M22 46h96c4 30-10 50-48 50S18 76 22 46z" fill="#1d1624" stroke={INK} strokeWidth="2.6" />
    <path d="M34 62c2 12 10 20 22 23" stroke="#4a3c57" strokeWidth="4" strokeLinecap="round" fill="none" />
    <ellipse cx="70" cy="46" rx="50" ry="10" fill="#8be04e" stroke={INK} strokeWidth="2.6" />
    <ellipse cx="70" cy="46" rx="38" ry="5.4" fill="#b6f27a" opacity=".7" />
    <path d="M38 94l-6 10M102 94l6 10" stroke={INK} strokeWidth="5" strokeLinecap="round" />
  </g>
);

// The whole night: moon, witch, bats, hills and the scholar pumpkin. Used at
// the top of the game. 560 x 210.
export const NightScene = () => (
  <svg className="hw-scene" viewBox="0 0 560 210" role="img" aria-label="A witch flies past the moon while a pumpkin in a graduation cap looks on">
    <g fill="#f5f0e1">
      {[
        [30, 30, 1.4], [92, 18, 1], [150, 44, 1.2], [214, 22, 1.6], [262, 56, 1], [310, 18, 1.2],
        [520, 30, 1.4], [540, 100, 1], [48, 96, 1], [196, 92, 1.2], [300, 98, 1], [500, 158, 1.2],
      ].map(([cx, cy, r]) => (
        <circle key={`${cx}-${cy}`} className="hw-star" cx={cx} cy={cy} r={r} style={{ animationDelay: `${(cx % 7) * 0.4}s` }} />
      ))}
    </g>
    <circle cx="420" cy="84" r="66" fill="#f3ec7a" />
    <circle cx="420" cy="84" r="66" fill="none" stroke="#f5b041" strokeWidth="3" opacity=".6" />
    <g fill="#e2d75e" opacity=".75">
      <circle cx="392" cy="62" r="10" />
      <circle cx="444" cy="106" r="14" />
      <circle cx="452" cy="58" r="6" />
      <circle cx="398" cy="114" r="5" />
    </g>
    <g className="hw-witch">
      <g transform="translate(338 44) scale(1.25)">
        <Witch />
      </g>
    </g>
    <Bat x={300} y={40} scale={1.3} />
    <Bat x={480} y={150} scale={1.1} delay={0.3} />
    <Bat x={250} y={74} scale={0.9} delay={0.6} />
    <path d="M0 176c60-26 120-28 190-12s130 4 200-14 120-6 170 8v52H0z" fill="#241b2d" />
    <path d="M0 192c80-18 160-14 250 0s190 6 310-6v24H0z" fill="#1b1422" />
    {/* Here lies boring training. */}
    <g transform="translate(214 104) rotate(-5 32 50)">
      <path d="M4 92V30C4 13 17 2 32 2s28 11 28 28v62z" fill="#5c4f66" stroke={INK} strokeWidth="2.6" />
      <path d="M10 88V31C10 18 20 9 32 9" stroke="#7a6c85" strokeWidth="3" fill="none" strokeLinecap="round" />
      <g fill="#f5f0e1" fontFamily="Open Sans, sans-serif" fontWeight="800" textAnchor="middle">
        <text x="32" y="34" fontSize="12" letterSpacing="1">R.I.P.</text>
        <text x="32" y="52" fontSize="8.6" letterSpacing=".5">BORING</text>
        <text x="32" y="63" fontSize="8.6" letterSpacing=".5">TRAINING</text>
      </g>
      <path d="M-4 92c10-6 22-6 32-2s26 4 40-1v6H-4z" fill="#3b2f45" />
    </g>
    <g transform="translate(330 150)">
      <LittlePumpkin />
    </g>
    <g className="hw-scholar" transform="translate(36 60) scale(1.25)">
      <ScholarPumpkin />
    </g>
  </svg>
);

// The witch crossing a small moon: a header for the riddles.
export const WitchMoon = () => (
  <svg className="hw-badge" viewBox="0 0 150 82" aria-hidden="true">
    <circle cx="98" cy="42" r="36" fill="#f3ec7a" />
    <g fill="#e2d75e" opacity=".75">
      <circle cx="84" cy="28" r="6" />
      <circle cx="110" cy="58" r="8" />
    </g>
    <g className="hw-witch">
      <g transform="translate(40 14) scale(.9)">
        <Witch />
      </g>
    </g>
    <Bat x={8} y={22} scale={0.9} />
  </svg>
);

// The small pumpkin on the corner button.
export const CornerPumpkin = () => (
  <svg viewBox="0 0 60 60" aria-hidden="true">
    <path d="M30 14c0-5 3-9 7-10" stroke="#4f7a2c" strokeWidth="3.4" strokeLinecap="round" fill="none" />
    <ellipse cx="19" cy="35" rx="14" ry="18" fill="#e5600a" stroke={INK} strokeWidth="2" />
    <ellipse cx="41" cy="35" rx="14" ry="18" fill="#e5600a" stroke={INK} strokeWidth="2" />
    <ellipse cx="30" cy="35" rx="13" ry="19" fill="#ff7a1a" stroke={INK} strokeWidth="2" />
    <g fill="#ffd166">
      <path d="M20 31l4-6 4 6z" />
      <path d="M32 31l4-6 4 6z" />
      <path d="M18 39c4 7 20 7 24 0l-3 .2-2 2.4-2.6-2.2-2.4 2.6-2.4-2.6-2.6 2.2-2-2.4z" />
    </g>
  </svg>
);
